import React, { useState, useEffect } from "react";
import { ArrowLeft, DollarSign, Users, TrendingUp, Copy, Check, Loader2, Clock, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Earnings() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/referral/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setStats(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [token]);

  const copyReferralLink = async () => {
    if (!stats) return;
    const link = `${window.location.origin}?ref=${stats.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast({ title: "Copied!", description: "Referral link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Error", description: "Couldn't copy link", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="earnings-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <DollarSign className="h-5 w-5 text-green-600" />
        <h1 className="font-bold text-gray-900">My Earnings</h1>
      </div>

      <div className="px-4 py-4 space-y-4 max-w-md mx-auto">
        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-green-600 to-green-700 border-0 text-white">
          <CardContent className="p-5">
            <p className="text-green-100 text-xs font-medium mb-1">Available Balance</p>
            <p className="text-4xl font-bold">${(stats?.available_balance || 0).toFixed(2)}</p>
            <div className="flex items-center gap-2 mt-3">
              {stats?.can_cashout ? (
                <Button size="sm" className="bg-white text-green-700 hover:bg-green-50 text-xs font-bold" data-testid="cashout-btn">
                  Cash Out
                </Button>
              ) : (
                <p className="text-green-200 text-xs">
                  ${(50 - (stats?.available_balance || 0)).toFixed(2)} more to reach $50 minimum cashout
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <Users className="h-4 w-4 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{stats?.total_referrals || 0}</p>
              <p className="text-[10px] text-gray-500">Referrals</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Zap className="h-4 w-4 text-amber-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{stats?.active_subscribers || 0}</p>
              <p className="text-[10px] text-gray-500">Active Subs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <TrendingUp className="h-4 w-4 text-green-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">${(stats?.monthly_earning || 0).toFixed(2)}</p>
              <p className="text-[10px] text-gray-500">/month</p>
            </CardContent>
          </Card>
        </div>

        {/* Total Earned */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Lifetime Earnings</p>
              <p className="text-2xl font-bold text-gray-900">${(stats?.total_earned || 0).toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-200" />
          </CardContent>
        </Card>

        {/* Referral Link */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-green-800 mb-2">Your Referral Link</p>
            <p className="text-xs text-green-600 mb-3">
              Share deals or this link. Earn <strong>$1.00/month</strong> for every friend who subscribes — for as long as they stay!
            </p>
            <div className="flex gap-2">
              <div className="flex-1 bg-white rounded-lg border border-green-200 px-3 py-2 text-xs text-gray-600 truncate">
                {window.location.origin}?ref={stats?.referral_code || "..."}
              </div>
              <Button
                size="sm"
                className={`px-3 text-xs ${copied ? "bg-green-600" : "bg-green-500 hover:bg-green-600"} text-white`}
                onClick={copyReferralLink}
                data-testid="copy-referral-link"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-gray-900 mb-3">How It Works</p>
            <div className="space-y-3">
              {[
                { step: "1", text: "Share deals with your referral link attached" },
                { step: "2", text: "Friends sign up through your link (14-day free trial)" },
                { step: "3", text: "When they subscribe, you earn $1.00/month — forever" },
                { step: "4", text: "Cash out when your balance reaches $50.00" },
              ].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-green-700">{s.step}</span>
                  </div>
                  <p className="text-sm text-gray-600">{s.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        {stats?.referrals?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-gray-900 mb-3">Your Referrals</p>
              <div className="space-y-2">
                {stats.referrals.map((ref, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white text-xs font-bold">
                        {ref.name[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{ref.name}</p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={`text-[10px] ${
                        ref.status === "active" ? "bg-green-100 text-green-700" :
                        ref.status === "trial" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {ref.status}
                      </Badge>
                      {ref.total_earned > 0 && (
                        <p className="text-xs text-green-600 font-medium mt-0.5">+${ref.total_earned.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
